const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

interface RequestOptions extends RequestInit {
  skipAuth?: boolean;
}

class ApiService {
  private accessToken: string | null = null;
  private refreshPromise: Promise<boolean> | null = null;

  setAccessToken(token: string | null) {
    this.accessToken = token;
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  private async request<T>(
    endpoint: string,
    options: RequestOptions = {},
  ): Promise<T> {
    const { skipAuth = false, ...fetchOptions } = options;

    const headers: HeadersInit = {
      ...fetchOptions.headers,
    };

    // Only set Content-Type for requests with a body (Fastify 5 is strict about this)
    if (fetchOptions.body) {
      (headers as Record<string, string>)['Content-Type'] = 'application/json';
    }

    if (!skipAuth && this.accessToken) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.accessToken}`;
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...fetchOptions,
      headers,
      credentials: 'include', // Include cookies for refresh token
    });

    if (response.status === 401 && !skipAuth) {
      // Try to refresh token (only once, avoid infinite loops)
      const refreshed = await this.refreshToken();
      if (refreshed) {
        // Update authorization header with new token and retry ONCE
        const retryHeaders: HeadersInit = {
          'Content-Type': 'application/json',
          ...fetchOptions.headers,
          'Authorization': `Bearer ${this.accessToken}`,
        };

        const retryResponse = await fetch(`${API_BASE_URL}${endpoint}`, {
          ...fetchOptions,
          headers: retryHeaders,
          credentials: 'include',
        });

        if (!retryResponse.ok) {
          const error = await retryResponse.json().catch(() => ({}));
          throw new ApiError(
            error.message || 'Request failed',
            retryResponse.status,
            error.code,
            error.details,
          );
        }

        if (retryResponse.status === 204) {
          return undefined as T;
        }

        const data = await retryResponse.json();
        return data.data ?? data;
      }
      throw new ApiError('Unauthorized', 401);
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new ApiError(
        error.message || 'Request failed',
        response.status,
        error.code,
        error.details,
      );
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return undefined as T;
    }

    const data = await response.json();
    return data.data ?? data;
  }

  async refreshToken(): Promise<boolean> {
    // If a refresh is already in progress, wait for it
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Start a new refresh
    this.refreshPromise = this.doRefreshToken();

    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async doRefreshToken(): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        this.accessToken = null;
        return false;
      }

      const responseData = await response.json();
      // Unwrap the { data: { accessToken } } structure from TransformInterceptor
      const tokenData = responseData.data ?? responseData;

      // Validate that we actually got a token
      if (!tokenData.accessToken || typeof tokenData.accessToken !== 'string') {
        this.accessToken = null;
        return false;
      }

      this.accessToken = tokenData.accessToken;
      return true;
    } catch {
      this.accessToken = null;
      return false;
    }
  }

  // Generic methods
  get<T>(endpoint: string, options?: RequestOptions) {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  post<T>(endpoint: string, body?: unknown, options?: RequestOptions) {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  put<T>(endpoint: string, body?: unknown, options?: RequestOptions) {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  patch<T>(endpoint: string, body?: unknown, options?: RequestOptions) {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  delete<T>(endpoint: string, options?: RequestOptions) {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const api = new ApiService();

// Import types
import type {
  AllowlistResponse,
  AllowedEmailEntry,
  UsersResponse,
  UserListItem,
  DeviceActivationInfo,
  DeviceAuthorizationResponse,
  PersonalAccessTokensResponse,
  CreateTokenResponse,
  CalendarSyncConfig,
  CalendarSyncLog,
  CalendarSyncLogsResponse,
  GoogleCalendarListItem,
  CalendarUploadResponse,
  MessageQueryParams,
  PaginatedMessages,
  UserDevice,
} from '../types';

// Allowlist API
export async function getAllowlist(params?: {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: 'all' | 'pending' | 'claimed';
}): Promise<AllowlistResponse> {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', String(params.page));
  if (params?.pageSize) searchParams.set('pageSize', String(params.pageSize));
  if (params?.search) searchParams.set('search', params.search);
  if (params?.status) searchParams.set('status', params.status);

  return api.get<AllowlistResponse>(`/allowlist?${searchParams}`);
}

export async function addToAllowlist(
  email: string,
  notes?: string,
): Promise<AllowedEmailEntry> {
  return api.post<AllowedEmailEntry>('/allowlist', { email, notes });
}

export async function removeFromAllowlist(id: string): Promise<void> {
  await api.delete<void>(`/allowlist/${id}`);
}

// Users API
export async function getUsers(params?: {
  page?: number;
  pageSize?: number;
  search?: string;
  role?: string;
  isActive?: boolean;
}): Promise<UsersResponse> {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', String(params.page));
  if (params?.pageSize) searchParams.set('pageSize', String(params.pageSize));
  if (params?.search) searchParams.set('search', params.search);
  if (params?.role) searchParams.set('role', params.role);
  if (params?.isActive !== undefined)
    searchParams.set('isActive', String(params.isActive));

  return api.get<UsersResponse>(`/users?${searchParams}`);
}

export async function updateUser(
  id: string,
  data: { displayName?: string; isActive?: boolean },
): Promise<UserListItem> {
  return api.patch<UserListItem>(`/users/${id}`, data);
}

export async function updateUserRoles(
  id: string,
  roles: string[],
): Promise<UserListItem> {
  return api.put<UserListItem>(`/users/${id}/roles`, { roles });
}

// Device Activation API
export async function getDeviceActivationInfo(
  userCode: string,
): Promise<DeviceActivationInfo> {
  return api.get<DeviceActivationInfo>(`/auth/device/activate?code=${userCode}`);
}

export async function authorizeDevice(
  userCode: string,
  approve: boolean,
): Promise<DeviceAuthorizationResponse> {
  return api.post<DeviceAuthorizationResponse>('/auth/device/authorize', {
    userCode,
    approve,
  });
}

// Personal Access Tokens API
export async function getTokens(params?: {
  page?: number;
  pageSize?: number;
}): Promise<PersonalAccessTokensResponse> {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', String(params.page));
  if (params?.pageSize) searchParams.set('pageSize', String(params.pageSize));

  return api.get<PersonalAccessTokensResponse>(`/tokens?${searchParams}`);
}

export async function createToken(
  name: string,
  expiresInHours: number,
): Promise<CreateTokenResponse> {
  return api.post<CreateTokenResponse>('/tokens', { name, expiresInHours });
}

export async function revokeToken(id: string): Promise<void> {
  await api.delete<void>(`/tokens/${id}`);
}

// Calendar Sync API
export async function getCalendarSyncConfig(): Promise<CalendarSyncConfig> {
  return api.get<CalendarSyncConfig>('/calendar/sync/config');
}

export async function updateCalendarSyncConfig(
  data: Partial<Pick<CalendarSyncConfig, 'enabled' | 'calendarId' | 'syncFrequencyMinutes'>>,
): Promise<CalendarSyncConfig> {
  return api.patch<CalendarSyncConfig>('/calendar/sync/config', data);
}

export async function triggerCalendarSync(): Promise<CalendarSyncLog> {
  return api.post<CalendarSyncLog>('/calendar/sync/trigger');
}

export async function getCalendarSyncLogs(params?: {
  page?: number;
  pageSize?: number;
  dateFilter?: string;
}): Promise<CalendarSyncLogsResponse> {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', String(params.page));
  if (params?.pageSize) searchParams.set('pageSize', String(params.pageSize));
  if (params?.dateFilter && params.dateFilter !== 'all') searchParams.set('dateFilter', params.dateFilter);
  return api.get<CalendarSyncLogsResponse>(`/calendar/sync/logs?${searchParams}`);
}

export async function getCalendarSyncLog(id: string): Promise<CalendarSyncLog> {
  return api.get<CalendarSyncLog>(`/calendar/sync/logs/${id}`);
}

export async function disconnectGoogleCalendar(): Promise<void> {
  await api.post<void>('/calendar/sync/auth/disconnect');
}

export async function getGoogleCalendars(): Promise<GoogleCalendarListItem[]> {
  return api.get<GoogleCalendarListItem[]>('/calendar/sync/calendars');
}

export async function resetCalendarSync(): Promise<CalendarSyncLog> {
  return api.post<CalendarSyncLog>('/calendar/sync/reset');
}

export function getGoogleCalendarAuthUrl(): string {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api';
  const token = api.getAccessToken();
  return `${baseUrl}/calendar/sync/auth/google?token=${encodeURIComponent(token || '')}`;
}

// Calendar Import API
export async function uploadCalendarJson(data: unknown): Promise<CalendarUploadResponse> {
  return api.post<CalendarUploadResponse>('/calendar/entries/upload', data);
}

// Device Text Messages API
export async function getDeviceTextMessages(params: MessageQueryParams): Promise<PaginatedMessages> {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', String(params.page));
  if (params.pageSize) searchParams.set('pageSize', String(params.pageSize));
  if (params.dateFrom) searchParams.set('dateFrom', params.dateFrom);
  if (params.dateTo) searchParams.set('dateTo', params.dateTo);
  if (params.sender) searchParams.set('sender', params.sender);
  if (params.deviceId) searchParams.set('deviceId', params.deviceId);
  if (params.deviceSimId) searchParams.set('deviceSimId', params.deviceSimId);
  return api.get<PaginatedMessages>(`/device-text-messages?${searchParams.toString()}`);
}

export async function getDeviceTextMessageSenders(): Promise<string[]> {
  return api.get<string[]>('/device-text-messages/senders');
}

export async function getDeviceTextMessageDevices(): Promise<UserDevice[]> {
  return api.get<UserDevice[]>('/device-text-messages/devices');
}
